import React, { useEffect, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import i18n from "i18next";
import { Shield, FileText, Database, Download, Globe, AlertCircle, Check, Send, Lock } from "lucide-react";

const TABS = [
    { id: "overview", get label() { return i18n.t("compliance.tab_overview"); }, icon: Shield },
    { id: "policies", label: "Policies", icon: FileText },
    { id: "subprocessors", label: "Sub-processors", icon: Database },
    { id: "dsar", label: "DSAR", icon: Send },
    { id: "export", label: "Data export", icon: Download },
];

export default function Compliance() {
    const { t } = useTranslation();
    const [tab, setTab] = useState("overview");

    return (
        <PageHeader title={t("pages.compliance_title")} subtitle={t("pages.compliance_subtitle")}>
            <div className="flex gap-1 border-b border-white/8 mb-6 overflow-x-auto">
                {TABS.map((tb) => {
                    const Icon = tb.icon;
                    const active = tab === tb.id;
                    return (
                        <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`compliance-tab-${tb.id}`}
                            className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors -mb-px ${active ? "border-[var(--jr-brand)] text-white" : "border-transparent text-[var(--jr-text-2)] hover:text-white"}`}>
                            <Icon size={14}/> {tb.label}
                        </button>
                    );
                })}
            </div>

            {tab === "overview" && <Overview/>}
            {tab === "policies" && <Policies/>}
            {tab === "subprocessors" && <SubProcessors/>}
            {tab === "dsar" && <DSAR/>}
            {tab === "export" && <Export/>}
        </PageHeader>
    );
}

function Overview() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/compliance/overview").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="text-[var(--jr-text-2)]">{i18n.t("common.loading")}</div>;
    return (
        <div className="grid md:grid-cols-3 gap-4">
            <KPI title="Compliance health" value={`${data.compliance_health}%`} icon={Check} accent="success"/>
            <KPI title="Data region" value={data.data_region} icon={Globe}/>
            <KPI title="AI Act class." value="Limited risk" icon={Shield} accent="amber"/>
            <KPI title={i18n.t("compliance.kpi_personal")} value={data.personal_records_total} icon={Database}/>
            <KPI title="Open DSARs" value={data.open_dsars} icon={Send}/>
            <KPI title="Audit events" value={data.audit_events_30d} icon={FileText}/>
            <div className="md:col-span-3 rounded-xl border border-[var(--jr-brand)]/30 bg-gradient-to-br from-[var(--jr-brand)]/10 to-transparent p-6">
                <Lock className="text-[var(--jr-brand)]" size={20}/>
                <h3 className="font-display text-xl tracking-tight mt-3">SOC 2 status: {data.soc2_status === "in_preparation" ? i18n.t("compliance.in_prep") : data.soc2_status}</h3>
                <p className="text-sm text-[var(--jr-text-2)] mt-2">{i18n.t("compliance.soc2_sub")}</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm text-[var(--jr-text-2)]">
                    <Item>Tamper-evident audit log ✓</Item>
                    <Item>{i18n.t("compliance.item_rbac")} ✓</Item>
                    <Item>Encryption at rest + in transit ✓</Item>
                    <Item>{i18n.t("compliance.item_dpas")} ✓</Item>
                </div>
            </div>
        </div>
    );
}

function Policies() {
    const [policies, setPolicies] = useState([]);
    useEffect(() => { api.get("/compliance/policies").then((r) => setPolicies(r.data)); }, []);
    return (
        <div className="space-y-3">
            {policies.map((p) => (
                <div key={p.id} data-testid={`policy-${p.id}`} className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-6">
                    <div className="flex items-center justify-between">
                        <h3 className="font-display text-xl tracking-tight">{p.title}</h3>
                        <div className="font-mono text-xs text-[var(--jr-text-3)] uppercase tracking-[0.16em]">v{p.version} · {p.effective_date}</div>
                    </div>
                    <p className="text-sm text-[var(--jr-text-2)] mt-3 leading-relaxed">{p.summary}</p>
                    <div className="mt-3 text-xs font-mono text-[var(--jr-text-3)] uppercase tracking-[0.16em]">Scope: <span className="text-[var(--jr-text-2)]">{p.scope}</span></div>
                </div>
            ))}
        </div>
    );
}

function SubProcessors() {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get("/compliance/sub-processors").then((r) => setRows(r.data)); }, []);
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden">
            <table className="w-full text-sm">
                <thead className="bg-[var(--jr-surface-2)] text-[var(--jr-text-3)] text-xs uppercase tracking-[0.16em]">
                    <tr><th className="text-left px-5 py-3 font-mono font-normal">Vendor</th><th className="text-left px-5 py-3 font-mono font-normal">Doel</th><th className="text-left px-5 py-3 font-mono font-normal">Locatie</th><th className="text-left px-5 py-3 font-mono font-normal">DPA</th></tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-t border-white/8">
                            <td className="px-5 py-3 font-medium text-white">{r.name}</td>
                            <td className="px-5 py-3 text-[var(--jr-text-2)]">{r.purpose}</td>
                            <td className="px-5 py-3 font-mono text-xs text-[var(--jr-text-2)]">{r.location}</td>
                            <td className="px-5 py-3">{r.dpa ? <Check size={14} className="text-[var(--jr-success)]"/> : <AlertCircle size={14} className="text-[var(--jr-amber)]"/>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function DSAR() {
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState({ requester_email: "", requester_name: "", request_type: "access", detail: "" });
    const [submitting, setSubmitting] = useState(false);
    const load = () => api.get("/compliance/dsar").then((r) => setRows(r.data));
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/compliance/dsar", form);
            toast.success(i18n.t("compliance.dsar_ok"));
            setForm({ requester_email: "", requester_name: "", request_type: "access", detail: "" });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setSubmitting(false); }
    };

    const complete = async (id) => {
        try { await api.post(`/compliance/dsar/${id}/complete`); toast.success(i18n.t("compliance.completed")); load(); }
        catch (e) { toast.error(i18n.t("common.failed")); }
    };

    return (
        <div className="grid lg:grid-cols-[420px_1fr] gap-6">
            <form onSubmit={submit} className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-6 space-y-4" data-testid="dsar-form">
                <h3 className="font-display text-xl tracking-tight">{i18n.t("compliance.dsar_new")}</h3>
                <Input label={i18n.t("search.th_name")} v={form.requester_name} onChange={(e) => setForm({...form, requester_name: e.target.value})} required testid="dsar-name"/>
                <Input label="Email" type="email" v={form.requester_email} onChange={(e) => setForm({...form, requester_email: e.target.value})} required testid="dsar-email"/>
                <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Type</label>
                    <select value={form.request_type} onChange={(e) => setForm({...form, request_type: e.target.value})} data-testid="dsar-type" className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                        <option value="access">Access (art. 15)</option>
                        <option value="deletion">Deletion (art. 17)</option>
                        <option value="rectification">Rectification (art. 16)</option>
                        <option value="portability">Portability (art. 20)</option>
                    </select>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Detail</label>
                    <textarea value={form.detail} onChange={(e) => setForm({...form, detail: e.target.value})} rows={3} data-testid="dsar-detail" className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
                </div>
                <button type="submit" disabled={submitting} data-testid="dsar-submit" className="w-full px-4 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">{submitting ? i18n.t("smarthunts.busy") : i18n.t("compliance.register")}</button>
            </form>

            <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[var(--jr-surface-2)] text-[var(--jr-text-3)] text-xs uppercase tracking-[0.16em]">
                        <tr><th className="text-left px-5 py-3 font-mono font-normal">{i18n.t("compliance.th_when")}</th><th className="text-left px-5 py-3 font-mono font-normal">{i18n.t("compliance.th_requester")}</th><th className="text-left px-5 py-3 font-mono font-normal">Type</th><th className="text-left px-5 py-3 font-mono font-normal">Status</th><th></th></tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td className="px-5 py-8 text-center text-[var(--jr-text-3)]" colSpan={5}>{i18n.t("compliance.no_dsars")}</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-white/8">
                                <td className="px-5 py-3 font-mono text-xs text-[var(--jr-text-3)]">{new Date(r.created_at).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" })}</td>
                                <td className="px-5 py-3 text-white">{r.requester_name}<div className="text-xs text-[var(--jr-text-2)]">{r.requester_email}</div></td>
                                <td className="px-5 py-3"><span className="text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded bg-white/5 text-[var(--jr-text-2)]">{r.request_type}</span></td>
                                <td className="px-5 py-3"><span className={`text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded ${r.status === "completed" ? "bg-[var(--jr-success)]/15 text-[var(--jr-success)]" : "bg-[var(--jr-amber)]/15 text-[var(--jr-amber)]"}`}>{r.status}</span></td>
                                <td className="px-5 py-3 text-right">{r.status !== "completed" && <button onClick={() => complete(r.id)} className="text-xs text-[var(--jr-brand)] hover:underline">Markeer afgehandeld</button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Export() {
    const [loading, setLoading] = useState(false);
    const download = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/compliance/export`, { headers: { Authorization: `Bearer ${getToken()}` } });
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "jeremai-export.json"; a.click(); URL.revokeObjectURL(url);
            toast.success(i18n.t("compliance.export_ok"));
        } catch (e) { toast.error(i18n.t("compliance.export_failed")); }
        finally { setLoading(false); }
    };
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-8 text-center">
            <Download className="text-[var(--jr-brand)] mx-auto" size={32}/>
            <h3 className="font-display text-2xl tracking-tight mt-4">GDPR data portability export</h3>
            <p className="text-sm text-[var(--jr-text-2)] max-w-xl mx-auto mt-3">{i18n.t("compliance.export_sub")}</p>
            <button onClick={download} disabled={loading} data-testid="data-export-btn" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--jr-brand)] text-white font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                <Download size={16}/> {loading ? i18n.t("compliance.generating") : "Download JSON"}
            </button>
        </div>
    );
}

function KPI({ title, value, icon: Icon, accent }) {
    const c = accent === "success" ? "text-[var(--jr-success)]" : accent === "amber" ? "text-[var(--jr-amber)]" : "text-[var(--jr-brand)]";
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5">
            <div className="flex items-center justify-between"><span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">{title}</span><Icon size={16} className={c}/></div>
            <div className="mt-3 font-display text-2xl tracking-tighter">{value}</div>
        </div>
    );
}

function Input({ label, v, onChange, type = "text", required, testid }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</label>
            <input value={v} onChange={onChange} type={type} required={required} data-testid={testid} className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none"/>
        </div>
    );
}

function Item({ children }) {
    return <div className="flex items-center gap-2"><Check size={14} className="text-[var(--jr-success)]"/><span>{children}</span></div>;
}
